"use client"

import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"

/* =========================================================
   STORAGE
========================================================= */

const STORAGE_KEY =
  "mixtape-listening-insights-v1"

const PLAY_THRESHOLD = 30

const AUTO_FLUSH_MS = 5000

/* =========================================================
   TYPES
========================================================= */

export type ListeningTrack = {
  id: string
  title: string
  artist?: string
  image?: string
  duration: number

  radioId?: string
  radioName?: string
  radioColor?: string
  radioLogo?: string
}

export type ListeningRadio = {
  id: string
  name: string
  color: string
  logo?: string
}

export type DayStats = {
  seconds: number
  tracksPlayed: number
  sessions: number
  active: boolean
}

export type TrackStats = {
  id: string
  title: string
  artist?: string
  image?: string
  seconds: number
  plays: number
  duration: number
  lastPlayedAt: string
}

export type RadioStats = {
  id: string
  name: string
  color: string
  logo?: string
  seconds: number
}

export type RecentItem = {
  id: string
  title: string
  artist?: string
  radio: string
  duration: number
  timestamp: string
  image?: string
}

export type StoredInsights = {
  days: Record<string, DayStats>
  tracks: Record<string, TrackStats>
  radios: Record<string, RadioStats>
  recent: RecentItem[]
  peakHours: Record<string, number>
}

/* =========================================================
   EMPTY
========================================================= */

const EMPTY_DATA: StoredInsights = {
  days: {},
  tracks: {},
  radios: {},
  recent: [],
  peakHours: {},
}

function cloneEmptyData(): StoredInsights {
  return {
    days: {},
    tracks: {},
    radios: {},
    recent: [],
    peakHours: {},
  }
}

/* =========================================================
   HELPERS
========================================================= */

function getDateKey(date = new Date()) {
  const year = date.getFullYear()

  const month = String(
    date.getMonth() + 1,
  ).padStart(2, "0")

  const day = String(
    date.getDate(),
  ).padStart(2, "0")

  return `${year}-${month}-${day}`
}

function loadData(): StoredInsights {
  if (typeof window === "undefined") {
    return cloneEmptyData()
  }

  try {
    const raw =
      localStorage.getItem(STORAGE_KEY)

    if (!raw) {
      return cloneEmptyData()
    }

    const parsed =
      JSON.parse(raw)

    return {
      days:
        parsed.days ?? {},

      tracks:
        parsed.tracks ?? {},

      radios:
        parsed.radios ?? {},

      recent:
        Array.isArray(
          parsed.recent,
        )
          ? parsed.recent
          : [],

      peakHours:
        parsed.peakHours ?? {},
    }
  } catch (error) {
    console.error(
      "[Insights] Error cargando datos:",
      error,
    )

    return cloneEmptyData()
  }
}

function saveData(
  data: StoredInsights,
) {
  if (
    typeof window ===
    "undefined"
  ) {
    return
  }

  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(data),
    )
  } catch (error) {
    console.error(
      "[Insights] Error guardando datos:",
      error,
    )
  }
}

/* =========================================================
   HOOK
========================================================= */

export function useListeningInsights() {
  const [data, setData] =
    useState<StoredInsights>(
      EMPTY_DATA,
    )

  const currentTrack =
    useRef<ListeningTrack | null>(
      null,
    )

  const startedAt =
    useRef<number | null>(null)

  /*
   * Tiempo total de la pista
   * que ya ha sido registrado.
   *
   * Esto evita contar dos veces
   * los mismos segundos.
   */
  const trackedSeconds =
    useRef(0)

  /*
   * Tiempo necesario para
   * considerar la pista como
   * reproducida.
   */
  const countedPlay =
    useRef(false)

  const mounted =
    useRef(false)

  /* =======================================================
     LOAD
  ======================================================= */

  useEffect(() => {
    const stored =
      loadData()

    setData(stored)

    mounted.current = true
  }, [])

  /* =======================================================
     UPDATE STORAGE + STATE
  ======================================================= */

  const updateStorage =
    useCallback(
      (
        updater: (
          current: StoredInsights,
        ) => StoredInsights,
      ) => {
        setData(current => {
          const next =
            updater(current)

          saveData(next)

          return next
        })
      },
      [],
    )

  /* =======================================================
     FLUSH
  ======================================================= */

  const flush =
    useCallback(() => {
      const track =
        currentTrack.current

      const started =
        startedAt.current

      if (
        !track ||
        started === null
      ) {
        return
      }

      const now =
        Date.now()

      /*
       * Tiempo real desde que
       * comenzó/reanudó la escucha.
       */
      const elapsed =
        Math.floor(
          (now - started) /
            1000,
        )

      /*
       * Solo registramos los
       * segundos que todavía
       * no habíamos registrado.
       */
      const newSeconds =
        elapsed -
        trackedSeconds.current

      if (
        newSeconds <= 0
      ) {
        return
      }

      trackedSeconds.current =
        elapsed

      const dateKey =
        getDateKey()

      const hour =
        new Date().getHours()

      updateStorage(
        current => {
          const next: StoredInsights =
            {
              days: {
                ...current.days,
              },

              tracks: {
                ...current.tracks,
              },

              radios: {
                ...current.radios,
              },

              recent: [
                ...current.recent,
              ],

              peakHours: {
                ...current.peakHours,
              },
            }

          /* ===============================================
             DAY
          =============================================== */

          const day =
            next.days[
              dateKey
            ] ?? {
              seconds: 0,
              tracksPlayed: 0,
              sessions: 0,
              active: false,
            }

          day.seconds +=
            newSeconds

          day.active = true

          /* ===============================================
             TRACK
          =============================================== */

          const existingTrack =
            next.tracks[
              track.id
            ] ?? {
              id: track.id,
              title:
                track.title,
              artist:
                track.artist,
              image:
                track.image,
              seconds: 0,
              plays: 0,
              duration:
                track.duration,
              lastPlayedAt:
                new Date().toISOString(),
            }

          existingTrack.seconds +=
            newSeconds

          existingTrack.lastPlayedAt =
            new Date().toISOString()

          /*
           * La reproducción cuenta
           * al llegar a 30 segundos.
           */
          if (
            !countedPlay.current &&
            trackedSeconds.current >=
              PLAY_THRESHOLD
          ) {
            existingTrack.plays +=
              1

            day.tracksPlayed +=
              1

            countedPlay.current =
              true
          }

          next.tracks[
            track.id
          ] =
            existingTrack

          /* ===============================================
             RADIO
          =============================================== */

          if (
            track.radioId
          ) {
            const existingRadio =
              next.radios[
                track.radioId
              ] ?? {
                id:
                  track.radioId,

                name:
                  track.radioName ??
                  "Radio",

                color:
                  track.radioColor ??
                  "#ffffff",

                logo:
                  track.radioLogo,

                seconds: 0,
              }

            existingRadio.seconds +=
              newSeconds

            next.radios[
              track.radioId
            ] =
              existingRadio
          }

          /* ===============================================
             PEAK HOURS
          =============================================== */

          const hourKey =
            String(hour)

          next.peakHours[
            hourKey
          ] =
            (
              next.peakHours[
                hourKey
              ] ?? 0
            ) +
            newSeconds

          /* ===============================================
             RECENT
          =============================================== */

          /*
           * Recent solamente se añade
           * cuando realmente cambia
           * la pista.
           */
          if (
            next.recent.length ===
              0 ||
            next.recent[0]
              .title !==
              track.title
          ) {
            const recentItem: RecentItem =
              {
                id:
                  `${track.id}-${Date.now()}`,

                title:
                  track.title,

                artist:
                  track.artist,

                radio:
                  track.radioName ??
                  "Mixtape",

                duration:
                  track.duration,

                timestamp:
                  new Date().toISOString(),

                image:
                  track.image,
              }

            next.recent.unshift(
              recentItem,
            )
          }

          next.recent =
            next.recent.slice(
              0,
              30,
            )

          /* ===============================================
             SAVE DAY
          =============================================== */

          next.days[
            dateKey
          ] = day

          return next
        },
      )
    }, [updateStorage])

  /* =========================================================
     START TRACK
  ========================================================= */

  const startTrack =
    useCallback(
      (
        track: ListeningTrack,
      ) => {
        if (!track?.id) {
          return
        }

        /*
         * Misma pista:
         * simplemente continuamos.
         */
        if (
          currentTrack.current
            ?.id === track.id
        ) {
          if (
            startedAt.current ===
            null
          ) {
            startedAt.current =
              Date.now()
          }

          return
        }

        /*
         * Guardar la pista anterior.
         */
        flush()

        /*
         * Nueva pista.
         */
        currentTrack.current =
          track

        startedAt.current =
          Date.now()

        trackedSeconds.current =
          0

        countedPlay.current =
          false

        /* ===============================================
           NEW SESSION
        =============================================== */

        updateStorage(
          current => {
            const dateKey =
              getDateKey()

            const day =
              current.days[
                dateKey
              ] ?? {
                seconds: 0,
                tracksPlayed: 0,
                sessions: 0,
                active: false,
              }

            return {
              ...current,

              days: {
                ...current.days,

                [dateKey]: {
                  ...day,

                  sessions:
                    day.sessions +
                    1,
                },
              },
            }
          },
        )
      },
      [
        flush,
        updateStorage,
      ],
    )

  /* =========================================================
     PAUSE
  ========================================================= */

  const pause =
    useCallback(() => {
      flush()

      startedAt.current =
        null
    }, [flush])

  /* =========================================================
     RESUME
  ========================================================= */

  const resume =
    useCallback(() => {
      if (
        !currentTrack.current
      ) {
        return
      }

      if (
        startedAt.current !==
        null
      ) {
        return
      }

      startedAt.current =
        Date.now()
    }, [])

  /* =========================================================
     STOP
  ========================================================= */

  const stop =
    useCallback(() => {
      flush()

      currentTrack.current =
        null

      startedAt.current =
        null

      trackedSeconds.current =
        0

      countedPlay.current =
        false
    }, [flush])

  /* =========================================================
     REFRESH
  ========================================================= */

  /*
   * Permite volver a leer localStorage
   * manualmente desde el dashboard.
   *
   * No es necesario para el tracking
   * normal, pero sirve para el botón
   * de refresh.
   */
  const refresh =
    useCallback(() => {
      const stored =
        loadData()

      setData(stored)

      return stored
    }, [])

  /* =========================================================
     AUTO FLUSH
  ========================================================= */

  useEffect(() => {
    if (!mounted.current) {
      return
    }

    const interval =
      window.setInterval(() => {
        if (
          currentTrack.current &&
          startedAt.current !==
            null
        ) {
          flush()
        }
      }, AUTO_FLUSH_MS)

    return () => {
      window.clearInterval(
        interval,
      )
    }
  }, [flush])

  /* =========================================================
     VISIBILITY
  ========================================================= */

  useEffect(() => {
    const handleVisibility =
      () => {
        if (
          document.visibilityState ===
          "hidden"
        ) {
          flush()

          startedAt.current =
            null
        }
      }

    document.addEventListener(
      "visibilitychange",
      handleVisibility,
    )

    return () => {
      document.removeEventListener(
        "visibilitychange",
        handleVisibility,
      )
    }
  }, [flush])

  /* =========================================================
     BEFORE UNLOAD
  ========================================================= */

  useEffect(() => {
    const handleUnload =
      () => {
        flush()
      }

    window.addEventListener(
      "beforeunload",
      handleUnload,
    )

    return () => {
      window.removeEventListener(
        "beforeunload",
        handleUnload,
      )
    }
  }, [flush])

  /* =========================================================
     RESET
  ========================================================= */

  const reset =
    useCallback(() => {
      currentTrack.current =
        null

      startedAt.current =
        null

      trackedSeconds.current =
        0

      countedPlay.current =
        false

      const empty =
        cloneEmptyData()

      setData(empty)

      if (
        typeof window !==
        "undefined"
      ) {
        localStorage.removeItem(
          STORAGE_KEY,
        )
      }
    }, [])

  /* =========================================================
     RETURN
  ========================================================= */

  return {
    data,

    startTrack,

    pause,

    resume,

    stop,

    refresh,

    reset,
  }
}